import { useQuery } from '@tanstack/react-query'

import req from '@sportninja/common/api/request'
import type {
  FacilityRef,
  GameLike,
  VenueRef,
} from '@sportninja/common/features/game/gameListView/types'

export const VENUE_OPTIONS_KEY = 'scheduleVenueOptions'

interface VenuesResponse {
  data?: VenueRef[]
}

/**
 * The EDITABLE venue/facility options.
 *
 * `/venues?schedule_id=…` returns every venue attached to the schedule or its
 * organization, unpaginated, with all facilities. The filter dropdown's
 * `/schedules/:id/venues/active` cannot serve this job: it is paginated, it
 * only lists venues games ALREADY use, and it filters each venue's facilities
 * down to the ones already in use — so an admin could never move a game to a
 * surface nobody had booked yet, which is the whole point of the feature.
 */
const fetchVenueOptions = async (
  scheduleId: string,
  signal?: AbortSignal
): Promise<VenueRef[]> => {
  const response = (await req('/venues', {
    query: { schedule_id: scheduleId },
    signal,
  })) as VenuesResponse
  return response?.data ?? []
}

const mergeFacilities = (
  existing: FacilityRef[] | null | undefined,
  incoming: FacilityRef[] | null | undefined
): FacilityRef[] => {
  const byId = new Map<string, FacilityRef>()
  for (const facility of existing ?? []) {
    byId.set(facility.id, facility)
  }
  for (const facility of incoming ?? []) {
    //  Prefer whichever copy actually carries a name — a game row's facility is
    //  named, the options list's is too, but one of them can be a bare id.
    const previous = byId.get(facility.id)
    byId.set(facility.id, {
      ...previous,
      ...facility,
      name: facility.name ?? previous?.name ?? null,
    })
  }
  return Array.from(byId.values())
}

const addVenue = (map: Map<string, VenueRef>, venue?: VenueRef | null) => {
  if (venue == null || !venue.id) {
    return
  }
  const previous = map.get(venue.id)
  map.set(venue.id, {
    ...previous,
    ...venue,
    timezone: venue.timezone ?? previous?.timezone ?? null,
    facilities: mergeFacilities(previous?.facilities, venue.facilities),
  })
}

export interface UseVenueOptionsResult {
  /** Union of every venue this view knows about, keyed by uid. */
  venuesById: Map<string, VenueRef>
  venues: VenueRef[]
  isLoading: boolean
}

/**
 * Unions three sources so a partial response can never make a KNOWN facility
 * look unknown:
 *
 * 1. the full options list for the schedule,
 * 2. the active-venue list the filter dropdown already loaded,
 * 3. the venue and facility riding on every loaded game.
 *
 * (3) matters most: the Day view groups games into venue columns, and a game
 * whose facility is missing from the options response would otherwise be filed
 * under "Unassigned" despite naming its surface right there on the row.
 */
export const useVenueOptions = (
  scheduleId: string | null | undefined,
  activeVenues: VenueRef[] = [],
  loadedGames: GameLike[] = []
): UseVenueOptionsResult => {
  'use memo'

  const query = useQuery({
    queryKey: [VENUE_OPTIONS_KEY, scheduleId ?? null],
    queryFn: ({ signal }) => fetchVenueOptions(scheduleId as string, signal),
    enabled: Boolean(scheduleId),
    staleTime: 5 * 60_000,
  })

  const venuesById = new Map<string, VenueRef>()

  for (const venue of query.data ?? []) {
    addVenue(venuesById, venue)
  }
  for (const venue of activeVenues) {
    addVenue(venuesById, venue)
  }
  for (const game of loadedGames) {
    if (game.venue == null) {
      continue
    }
    addVenue(venuesById, {
      ...game.venue,
      facilities: game.facility ? [game.facility] : game.venue.facilities,
    })
  }

  return {
    venuesById,
    venues: Array.from(venuesById.values()),
    isLoading: query.isLoading,
  }
}
